import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Copy, UserPlus, Mail, ShieldCheck } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { env } from '@/lib/env';

export const Route = createFileRoute('/_app/team')({
  staticData: { crumb: 'Zespół (dostęp)' },
  component: TeamPage,
});

type Role = 'owner' | 'manager' | 'receptionist' | 'read_only';

interface Member {
  user_id: string;
  email: string;
  full_name: string | null;
  role: Role;
  created_at: number;
  last_login_at: number | null;
}

interface Invitation {
  id: string;
  email: string;
  role: Role;
  token: string;
  expires_at: number;
  created_at: number;
}

const ROLE_LABEL: Record<Role, string> = {
  owner: 'Właściciel',
  manager: 'Menedżer',
  receptionist: 'Recepcja',
  read_only: 'Tylko podgląd',
};

const ROLE_HINT: Record<Role, string> = {
  owner: 'Pełny dostęp + zarządzanie subskrypcją',
  manager: 'Treść, kalendarz, komunikacja — bez billing',
  receptionist: 'Tylko kalendarz: rezerwacje, no-show, przypomnienia',
  read_only: 'Podgląd dashboardów, bez zmian',
};

function TeamPage() {
  const { session } = Route.useRouteContext();
  if (session?.role === 'operator') return null;
  const isOwner = session?.role === 'owner';

  const qc = useQueryClient();
  const members = useQuery({
    queryKey: ['admin', 'team', 'members'],
    queryFn: async (): Promise<Member[]> => {
      const res = await fetch(`${env.apiBaseUrl}/api/admin/team`, { credentials: 'include' });
      if (!res.ok) throw new Error('failed_to_load');
      const j = (await res.json()) as { members: Member[] };
      return j.members;
    },
  });
  const invitations = useQuery({
    queryKey: ['admin', 'team', 'invitations'],
    enabled: isOwner,
    queryFn: async (): Promise<Invitation[]> => {
      const res = await fetch(`${env.apiBaseUrl}/api/admin/team/invitations`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('failed_to_load');
      const j = (await res.json()) as { invitations: Invitation[] };
      return j.invitations;
    },
  });

  const invite = useMutation({
    mutationFn: async (input: { email: string; role: Role }) => {
      const res = await fetch(`${env.apiBaseUrl}/api/admin/team/invitations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'invite_failed');
      }
      return (await res.json()) as {
        accept_url: string;
        expires_at: number;
        email_sent?: boolean;
        email_reason?: string;
      };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team', 'invitations'] }),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/admin/team/invitations/${id}/revoke`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw new Error('revoke_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team', 'invitations'] }),
  });

  const setRole = useMutation({
    mutationFn: async (input: { userId: string; role: Role }) => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/admin/team/members/${input.userId}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ role: input.role }),
        }
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'role_change_failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team', 'members'] }),
  });

  const remove = useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`${env.apiBaseUrl}/api/admin/team/members/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('remove_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team', 'members'] }),
  });

  const [showInvite, setShowInvite] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState('');
  const [newRole, setNewRole] = React.useState<Role>('manager');
  const [issuedInvite, setIssuedInvite] = React.useState<{ url: string; email: string; emailSent: boolean } | null>(
    null
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Zespół (dostęp do panelu)
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Zarządzaj pracownikami kliniki, którzy mogą logować się do tego panelu.
          Każdy ma rolę określającą zakres uprawnień.
        </p>
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>Członkowie</CardTitle>
            <CardDescription>
              {members.data?.length ?? 0} osób z dostępem do tej kliniki.
            </CardDescription>
          </div>
          {isOwner ? (
            <Button type="button" onClick={() => setShowInvite(true)}>
              <UserPlus className="size-4" aria-hidden="true" /> Zaproś
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="p-0">
          {members.data && members.data.length > 0 ? (
            <ul role="list" className="divide-y divide-border">
              {members.data.map((m) => (
                <li
                  key={m.user_id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-surface/60"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-text">
                      {m.full_name || m.email}
                      {m.user_id === session?.user.id ? (
                        <Badge variant="soft" className="ml-2 text-[10px]">to Ty</Badge>
                      ) : null}
                    </p>
                    <p className="text-xs text-text-muted">{m.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isOwner ? (
                      <Select
                        value={m.role}
                        onChange={(e) =>
                          setRole.mutate({ userId: m.user_id, role: e.target.value as Role })
                        }
                        className="h-8 w-40"
                      >
                        {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <RoleBadge role={m.role} />
                    )}
                    {isOwner && m.user_id !== session?.user.id ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button type="button" size="icon" variant="ghost" aria-label="Usuń">
                            <Trash2 className="size-4" aria-hidden="true" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Usunąć dostęp {m.email}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Osoba straci możliwość logowania do panelu kliniki. Ta operacja
                              jest natychmiastowa — można ją odtworzyć przez ponowne zaproszenie.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Anuluj</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove.mutate(m.user_id)}
                            >
                              Usuń
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : members.isPending ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : (
            <p className="p-4 text-sm text-text-muted">Brak członków zespołu.</p>
          )}
        </CardContent>
      </Card>

      {isOwner ? (
        <Card>
          <CardHeader>
            <CardTitle>Pending zaproszenia</CardTitle>
            <CardDescription>
              Linki wygasają po 7 dniach. Skopiuj i wyślij właściwej osobie.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invitations.data && invitations.data.length > 0 ? (
              <ul role="list" className="space-y-2">
                {invitations.data.map((i) => (
                  <InvitationRow key={i.id} inv={i} onRevoke={() => revoke.mutate(i.id)} />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-text-muted">Brak pending zaproszeń.</p>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Role i uprawnienia</CardTitle>
          <CardDescription>Co każda rola może robić.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul role="list" className="grid gap-2 md:grid-cols-2">
            {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
              <li
                key={r}
                className="rounded-md bg-surface p-3 ring-1 ring-black/5"
              >
                <p className="text-sm font-semibold text-text">{ROLE_LABEL[r]}</p>
                <p className="mt-0.5 text-xs text-text-muted">{ROLE_HINT[r]}</p>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {isOwner ? (
        <InviteDialog
          open={showInvite}
          onOpenChange={(open) => {
            setShowInvite(open);
            if (!open) setIssuedInvite(null);
          }}
          email={newEmail}
          role={newRole}
          onEmailChange={setNewEmail}
          onRoleChange={setNewRole}
          onIssue={async () => {
            try {
              const r = await invite.mutateAsync({ email: newEmail, role: newRole });
              setIssuedInvite({
                url: r.accept_url,
                email: newEmail,
                emailSent: r.email_sent === true,
              });
              toast.success('Link zaproszenia wystawiony');
            } catch (e) {
              toast.error((e as Error).message);
            }
          }}
          issued={issuedInvite}
          busy={invite.isPending}
        />
      ) : null}
    </div>
  );
}

function RoleBadge({ role }: { role: Role }) {
  return <Badge variant="default">{ROLE_LABEL[role]}</Badge>;
}

function InvitationRow({ inv, onRevoke }: { inv: Invitation; onRevoke: () => void }) {
  const acceptUrl = `${window.location.origin}/team/accept/${inv.token}`;
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(acceptUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-md bg-surface p-3 ring-1 ring-black/5">
      <Mail className="size-4 text-text-muted" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text">{inv.email}</p>
        <p className="text-xs text-text-muted">
          {ROLE_LABEL[inv.role]} · wygasa {new Date(inv.expires_at * 1000).toLocaleString('pl-PL')}
        </p>
      </div>
      <Input readOnly value={acceptUrl} onFocus={(e) => e.currentTarget.select()} className="h-8 w-72 font-mono text-xs" />
      <Button type="button" size="sm" variant="secondary" onClick={copy}>
        <Copy className="size-3.5" aria-hidden="true" />
        {copied ? 'Skopiowano' : 'Kopiuj'}
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={onRevoke}>
        Anuluj
      </Button>
    </li>
  );
}

function InviteDialog({
  open,
  onOpenChange,
  email,
  role,
  onEmailChange,
  onRoleChange,
  onIssue,
  issued,
  busy,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  role: Role;
  onEmailChange: (v: string) => void;
  onRoleChange: (v: Role) => void;
  onIssue: () => Promise<void>;
  issued: { url: string; email: string; emailSent: boolean } | null;
  busy: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Zaproś osobę do zespołu</DialogTitle>
          <DialogDescription>
            Wysyłamy e-mail z linkiem aktywacyjnym (7 dni). Link możesz też skopiować i przesłać innym kanałem.
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          <div className="space-y-3">
            <p
              className={
                issued.emailSent
                  ? 'rounded-md bg-success/10 p-3 text-sm text-success ring-1 ring-success/20'
                  : 'rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20'
              }
            >
              {issued.emailSent ? (
                <>
                  Wysłaliśmy e-mail z zaproszeniem na <strong>{issued.email}</strong>.
                  Link wygasa za 7 dni. Możesz też skopiować i przesłać go bezpośrednio.
                </>
              ) : (
                <>
                  Zaproszenie wystawione dla <strong>{issued.email}</strong>, ale wysyłka e-maila
                  się nie powiodła. Skopiuj link i prześlij ręcznie.
                </>
              )}
            </p>
            <Input readOnly value={issued.url} onFocus={(e) => e.currentTarget.select()} />
            <Button
              type="button"
              variant="secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(issued.url);
                  toast.success('Skopiowano link');
                } catch {
                  /* noop */
                }
              }}
            >
              <Copy className="size-4" aria-hidden="true" /> Kopiuj link
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="E-mail" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder="osoba@klinika.pl"
              />
            </Field>
            <Field label="Rola" htmlFor="role">
              <Select id="role" value={role} onChange={(e) => onRoleChange(e.target.value as Role)}>
                {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]} — {ROLE_HINT[r]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        )}

        {!issued ? (
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Anuluj
            </Button>
            <Button type="button" onClick={() => onIssue()} disabled={!email || busy}>
              <ShieldCheck className="size-4" aria-hidden="true" /> Wystaw link
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
